"use client";

import SocialIcons from "../socialIcons/SocialIcons";
import Link from "next/link";
import Image from "next/image";
import Ticker from "../tricker/Tricker";
import "./style.css";
import { useEffect, useState } from "react";
import FooterSkeleton from "../skeletons/FooterSkeleton";
import ScrollTopButton from "@/components/scrollTopButton/ScrollTopButton";
import CollaborationMarquee from "@/components/collaborationmarquee/Collaborationmarquee";
import type { MenuItem, MenuResponse } from "@/types/menu";

export default function Footer() {
  const [menuData, setMenuData] = useState<MenuItem[]>([]);
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);

  const [newsletterData, setNewsletterData] = useState({
    name: "",
    email: "",
  });

  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});
  const newsErrors: any = {};
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const handleNewsletterChange = (field: string, value: string) => {
    setNewsletterData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();

    const newsErrors: any = {};

    if (!newsletterData.name.trim() || newsletterData.name.length < 3) {
      newsErrors.name = "Name must be 3 letters!";
    }

    if (!newsletterData.email.trim()) {
      newsErrors.email = "Enter Email!";
    } else if (!emailRegex.test(newsletterData.email)) {
      newsErrors.email = "Enter valid email!";
    }

    if (Object.keys(newsErrors).length) {
      setErrors(newsErrors);
      return;
    }

    try {
      const res = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newsletterData),
      });

      const data = await res.json();

      // ✅ Show success state instead of form
      setSubscribed(true);
      setNewsletterData({ name: "", email: "" });
      setErrors({});
    } catch (err) {
      console.error(err);
    }
  };

  // useEffect(() => {
  //   const fetchData = async () => {
  //     try {
  //       const res = await fetch("/api/menu");
  //       const result: MenuResponse = await res.json();
  //       setMenuData(result.data || []);
  //     } catch (error) {
  //       console.error(error);
  //     }
  //   };

  //   fetchData();
  // }, []);

  // Parent items only

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch("/api/menu");
        const result: MenuResponse = await res.json();
        setMenuData(result.data || []);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const parentMenus = (menuData ?? [])
    ?.filter(
      (item) =>
        String(item.item_parent_id) === "0" &&
        item.item_visibility === "1" &&
        item.item_location === "main",
    )
    ?.sort((a, b) => Number(a.item_order) - Number(b.item_order));

  const footerLinks = [
    {
      title: "WHO WE ARE",
      links: ["About Us", "Contact Us", "Advertising"],
    },
    {
      title: "CONTENTS",
      links: (parentMenus ?? []).map((e) => e), // ✅ fix
    },
    {
      title: "RESOURCES",
      links: ["Donation", "Sitemap"],
    },
  ];

  const slugify = (text: string) =>
    text.toLowerCase().trim().replace(/\s+/g, "-");

  if (loading) return <FooterSkeleton />;

  return (
    <>
      <div className="pt-5 pb-2">
        <footer className="px-3 pt-5">
          <div className="container-fluid px-md-5">
            <div className="row g-4">
              {/* Brand and Intro */}
              <div className="col-12 col-md-3">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <Link href="/" className="d-inline-flex align-items-center">
                    <Image
                      src={`/logo/logo-dark.png`}
                      height={40}
                      width={120}
                      alt="coinccino"
                      className="logo logo-light"
                      priority
                      fetchPriority="high"
                    />
                    <Image
                      src={`/logo/logo-light.png`}
                      height={40}
                      width={120}
                      alt="coinccino"
                      className="logo logo-dark"
                      priority
                      fetchPriority="high"
                    />
                  </Link>
                </div>

                <p className="mb-4">
                  The community for those passionate about blockchain and
                  innovative technologies, uniting passion, knowledge and
                  opportunity in the digital world
                </p>

                {/* Social Icons */}
                <div className="d-flex">
                  <SocialIcons />
                </div>
              </div>

              {/* Dynamic Link Columns */}
              {(footerLinks ?? []).map((section) => (
                <div
                  key={section.title}
                  className="col-6 col-md-2 footer-navigation"
                >
                  <h6 className="uppercase mb-3 footer-title">
                    {section.title}
                  </h6>
                  <ul className="list-unstyled d-flex flex-column gap-2 m-0">
                    {(section.links ?? []).map((link: any, index: number) => {
                      // If link is object (menu API)
                      if (typeof link === "object") {
                        return (
                          <li key={link.item_id}>
                            <Link
                              href={`/${link.item_slug}`}
                              className="no-underline"
                            >
                              {link.item_name}
                            </Link>
                          </li>
                        );
                      }

                      // If link is string
                      const slug = slugify(link);

                      return (
                        <li key={index}>
                          <Link href={`/${slug}`} className="no-underline">
                            {link}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}

              {/* Newsletter Section */}
              <div className="col-12 col-md-3">
                {subscribed ? (
                  <div className="newsletter-success text-center p-3">
                    <p
                      style={{
                        fontSize: "14px",
                        color: "var(--muted-foreground)",
                      }}
                    >
                      To complete your subscription, click the <br />
                      confirmation link in your inbox. If it <br />
                      doesn’t arrive within 3 minutes, check <br />
                      your spam folder.
                    </p>
                  </div>
                ) : (
                  <form
                    onSubmit={submitForm}
                    className="d-flex flex-column gap-3 newsletter-section text-center"
                  >
                    <input
                      type="text"
                      placeholder="Full Name"
                      className="form-control border-0 shadow-lg text-center"
                      // required
                      style={{
                        background: "#f1f3f5",
                        padding: "12px 16px",
                        fontSize: "14px",
                        borderRadius: "8px",
                      }}
                      value={newsletterData.name}
                      onChange={(e) =>
                        handleNewsletterChange("name", e.target.value)
                      }
                    />
                    <input
                      placeholder="Your Email Address"
                      className="form-control border-0 shadow-lg text-center"
                      style={{
                        background: "#f1f3f5",
                        padding: "12px 16px",
                        fontSize: "14px",
                        borderRadius: "8px",
                      }}
                      value={newsletterData.email}
                      onChange={(e) =>
                        handleNewsletterChange("email", e.target.value)
                      }
                    />

                    <button
                      type="submit"
                      className="btn w-100 text-white text-uppercase"
                      style={{
                        background: "var(--ghost-accent-color)",
                        borderRadius: "999px",
                        padding: "12px 16px",
                        fontSize: "12px",
                        letterSpacing: "2px",
                      }}
                    >
                      Subscribe
                    </button>
                    {(errors.name || errors.email) && (
                      <small className="text-2xl text-red-800 error">
                        {errors.name || errors.email}
                      </small>
                    )}
                  </form>
                )}
              </div>
            </div>

            {/* Featured Banner */}
            {/* <div className="pt-5">
            <p className="featured-banner">
              We are featured on <span>▼</span>
            </p>
          </div> */}
          </div>
        </footer>
        <CollaborationMarquee only="collaborated" />
        {/* <Ticker /> */}
        <div className="container py-5 text-center footer-copyright">
          <span className="footer-copyright-text">
            Copyright 2026 ©️ Coinccino - All Rights Reserved.
            <Link className="footer-copyright-link" href="/terms-of-services">
              {" "}
              Terms of Services
            </Link>{" "}
            &amp;{" "}
            <Link className="footer-copyright-link" href="/privacy-and-policy">
              {" "}
              Privacy Policy
            </Link>
            .<br />
          </span>
          {/* 
        <span className="footer-copyright-text">
          <Link href="#" className="termly-display-preferences">
            Consent Preferences
          </Link>
        </span> */}
        </div>
        <ScrollTopButton />
      </div>
    </>
  );
}
